/**
 * Detection / Rules - alert sources from the customer's own DLP, UEBA and EDR
 * tools (Bank II-11).
 *
 * Live data source:
 *   GET /v1/detection/external-alerts/sources → per tool: alerts in the last
 *       24 hours, 7 and 30 days, findings raised, alerts awaiting a finding,
 *       last received and a stale flag (nothing in 24 hours); for a CEF or
 *       LEEF tool FOCTTA does not recognise, one row per header vendor and
 *       product; the latest ingests with their counts; and the audit events
 *       and notifications not delivered yet, so an operator sees when ingest
 *       or delivery has stopped.
 *
 * Truthful scope: these are alerts the customer's tools already raised and
 * sent to FOCTTA (POST /v1/detection/external-alerts). FOCTTA does not run on
 * the endpoint and blocks nothing from them. Each alert becomes a decision on
 * Detection / Decisions.
 */

import { useEffect, useState } from 'react';
import { Loader2, RadioTower } from 'lucide-react';

import { GlassCard, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ApiError,
  listExternalAlertSources,
  type ExternalAlertBatch,
  type ExternalAlertDeliveryBacklog,
  type ExternalAlertSource,
} from '@/lib/api';

/** The tools the ingest reads, and how each is sent. Mirrors detection/external-alerts/parsers.ts. */
export const SUPPORTED_TOOLS: ReadonlyArray<{ vendor: string; name: string; kind: 'dlp' | 'ueba' | 'edr'; sentAs: string }> = [
  { vendor: 'microsoft_purview_dlp', name: 'Microsoft Purview DLP', kind: 'dlp', sentAs: 'Microsoft Graph security alert JSON' },
  { vendor: 'symantec_dlp', name: 'Symantec DLP', kind: 'dlp', sentAs: 'CEF syslog line' },
  { vendor: 'forcepoint_dlp', name: 'Forcepoint DLP', kind: 'dlp', sentAs: 'CEF syslog line' },
  { vendor: 'microsoft_sentinel_ueba', name: 'Microsoft Sentinel UEBA', kind: 'ueba', sentAs: 'BehaviorAnalytics row JSON' },
  { vendor: 'microsoft_purview_insider_risk', name: 'Microsoft Purview Insider Risk Management', kind: 'ueba', sentAs: 'Microsoft Graph security alert JSON' },
  { vendor: 'exabeam', name: 'Exabeam', kind: 'ueba', sentAs: 'CEF or LEEF line' },
  { vendor: 'securonix', name: 'Securonix', kind: 'ueba', sentAs: 'CEF or LEEF line' },
  { vendor: 'crowdstrike_falcon', name: 'CrowdStrike Falcon', kind: 'edr', sentAs: 'Alerts API alert JSON, or CEF' },
  { vendor: 'microsoft_defender_endpoint', name: 'Microsoft Defender for Endpoint', kind: 'edr', sentAs: 'Alert JSON (API or Graph), or CEF' },
  { vendor: 'sentinelone', name: 'SentinelOne', kind: 'edr', sentAs: 'Threat JSON, or CEF' },
  { vendor: 'cef_other', name: 'Another tool sending CEF', kind: 'dlp', sentAs: 'CEF line, kind declared with the batch' },
  { vendor: 'leef_other', name: 'Another tool sending LEEF', kind: 'dlp', sentAs: 'LEEF line, kind declared with the batch' },
];

const KIND_LABEL = { dlp: 'DLP', ueba: 'UEBA', edr: 'EDR' } as const;

function isUnitLimitedRefusal(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 403) return false;
  const type = (err.body as { type?: unknown } | null)?.type;
  return typeof type === 'string' && type.endsWith('/external_alerts_need_all_units');
}

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' }) + ' IST' : 'never';
}

/** The counts of every source row of one tool, added up; stale only when every row is. */
export function sumSources(rows: readonly ExternalAlertSource[]): Omit<ExternalAlertSource, 'sourceKind' | 'vendor' | 'sourceProduct'> | null {
  if (rows.length === 0) return null;
  const latest = (pick: (r: ExternalAlertSource) => string | null) =>
    rows.map(pick).reduce<string | null>((m, v) => (v !== null && (m === null || v > m) ? v : m), null);
  return {
    alerts24h: rows.reduce((t, r) => t + r.alerts24h, 0),
    alerts7d: rows.reduce((t, r) => t + r.alerts7d, 0),
    alerts30d: rows.reduce((t, r) => t + r.alerts30d, 0),
    findings7d: rows.reduce((t, r) => t + r.findings7d, 0),
    awaitingFinding: rows.reduce((t, r) => t + r.awaitingFinding, 0),
    lastReceivedAt: latest((r) => r.lastReceivedAt),
    lastOccurredAt: latest((r) => r.lastOccurredAt),
    stale: rows.every((r) => r.stale),
  };
}

function LastReceived({ c }: { c: Pick<ExternalAlertSource, 'lastReceivedAt' | 'stale'> | null }) {
  if (!c) return <>nothing received</>;
  return (
    <>
      {when(c.lastReceivedAt)}
      {c.stale && (
        <Badge variant="outline" className="ml-2 text-[9px] text-sev-high border-sev-high/40" data-testid="external-alert-source-stale">
          nothing in 24 h
        </Badge>
      )}
    </>
  );
}

function BacklogNote({ b }: { b: ExternalAlertDeliveryBacklog | undefined }) {
  if (!b) return null;
  const pending = b.auditEventsPending + b.notificationsPending;
  return (
    <div
      className={`glass-lite rounded-md p-2 text-xs ${pending > 0 ? 'text-sev-high' : 'text-muted-foreground'}`}
      role={pending > 0 ? 'alert' : undefined}
      data-testid="external-alerts-backlog"
    >
      {pending > 0
        ? `Not delivered yet: ${b.auditEventsPending} audit event${b.auditEventsPending === 1 ? '' : 's'} and ${b.notificationsPending} notification${
            b.notificationsPending === 1 ? '' : 's'
          } from earlier ingests (oldest ${when(b.oldestPendingAt)}). They are retried on the next ingest; if this number does not go down, the audit chain or notifications are failing. Contact support@foctta.com.`
        : 'Every audit event and notification of past ingests has been delivered.'}
    </div>
  );
}

export function ExternalAlertSourcesPanel() {
  const [data, setData] = useState<{
    sources: ExternalAlertSource[];
    recentBatches: ExternalAlertBatch[];
    deliveryBacklog?: ExternalAlertDeliveryBacklog;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unitLimited, setUnitLimited] = useState(false);

  useEffect(() => {
    let live = true;
    listExternalAlertSources()
      .then((r) => live && setData(r))
      .catch((err) => {
        if (!live) return;
        if (isUnitLimitedRefusal(err)) setUnitLimited(true);
        else setError(err instanceof Error ? err.message : 'load failed');
      });
    return () => {
      live = false;
    };
  }, []);

  const header = (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <RadioTower className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-base font-semibold">Alert sources: DLP, UEBA and EDR</h2>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="external-alerts-scope">
        These are alerts your own DLP, UEBA and EDR tools already raised and sent to FOCTTA. FOCTTA does not run on your endpoints and does not block, quarantine or
        change anything because of them. Each alert becomes a decision on Detection / Decisions, linked to the data FOCTTA DSPM knows on the same device.
      </p>
    </div>
  );

  if (unitLimited) {
    return (
      <div className="mt-8 space-y-4" data-testid="external-alerts-panel">
        {header}
        <GlassCard className="p-4">
          <CardDescription className="text-sm" data-testid="external-alerts-unit-limited">
            Alert batches span every business unit, so their counts are open to people who see all units. Your access is limited to some units; the findings for
            your units are on Detection / Decisions.
          </CardDescription>
        </GlassCard>
      </div>
    );
  }

  const byVendor = new Map<string, ExternalAlertSource[]>();
  for (const s of data?.sources ?? []) byVendor.set(s.vendor, [...(byVendor.get(s.vendor) ?? []), s]);
  return (
    <div className="mt-8 space-y-4" data-testid="external-alerts-panel">
      {header}
      {error && (
        <div className="glass-lite rounded-md p-2 text-xs text-sev-critical" role="alert">
          {error}
        </div>
      )}
      <BacklogNote b={data?.deliveryBacklog} />
      <GlassCard className="p-0 overflow-hidden">
        <div className="px-4 py-2 border-b border-border/40">
          <CardTitle className="text-sm">Sources</CardTitle>
          <CardDescription className="text-xs">
            A tool flagged &quot;nothing in 24 h&quot; sent no alert in the last 24 hours: check its forwarder if it normally sends every day.
          </CardDescription>
        </div>
        {data === null && !error ? (
          <div className="p-6 text-center text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
            Loading
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
                <th className="text-left px-4 py-2 font-medium">Tool</th>
                <th className="text-left px-2 py-2 font-medium">Kind</th>
                <th className="text-left px-2 py-2 font-medium">Sent as</th>
                <th className="text-right px-2 py-2 font-medium">Alerts 24 h</th>
                <th className="text-right px-2 py-2 font-medium">Alerts 7 d</th>
                <th className="text-right px-2 py-2 font-medium">Findings 7 d</th>
                <th className="text-right px-2 py-2 font-medium">Awaiting finding</th>
                <th className="text-left px-4 py-2 font-medium">Last received</th>
              </tr>
            </thead>
            <tbody>
              {SUPPORTED_TOOLS.map((t) => {
                const rows = byVendor.get(t.vendor) ?? [];
                const c = sumSources(rows);
                // An unrecognised CEF or LEEF tool: one row per header vendor and product under the summed row.
                const subRows = t.vendor.endsWith('_other') ? rows.filter((r) => r.sourceProduct !== null) : [];
                return [
                  <tr key={t.vendor} className="border-b border-border/30" data-testid="external-alert-source-row">
                    <td className="px-4 py-2 font-medium">{t.name}</td>
                    <td className="px-2 py-2">
                      {t.vendor.endsWith('_other') ? (
                        <span className="text-muted-foreground">as declared</span>
                      ) : (
                        <Badge variant="outline" className="text-[9px]">
                          {KIND_LABEL[t.kind]}
                        </Badge>
                      )}
                    </td>
                    <td className="px-2 py-2 text-muted-foreground">{t.sentAs}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{c?.alerts24h ?? 0}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{c?.alerts7d ?? 0}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{c?.findings7d ?? 0}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{c?.awaitingFinding ?? 0}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <LastReceived c={c} />
                    </td>
                  </tr>,
                  ...subRows.map((r) => (
                    <tr key={`${t.vendor}:${r.sourceProduct}:${r.sourceKind}`} className="border-b border-border/30" data-testid="external-alert-source-subrow">
                      <td className="pl-8 pr-4 py-1 font-mono text-[11px] text-muted-foreground">{r.sourceProduct}</td>
                      <td className="px-2 py-1">
                        <Badge variant="outline" className="text-[9px]">
                          {KIND_LABEL[r.sourceKind]}
                        </Badge>
                      </td>
                      <td className="px-2 py-1" />
                      <td className="px-2 py-1 text-right tabular-nums">{r.alerts24h}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{r.alerts7d}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{r.findings7d}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{r.awaitingFinding}</td>
                      <td className="px-4 py-1 whitespace-nowrap">
                        <LastReceived c={r} />
                      </td>
                    </tr>
                  )),
                ];
              })}
            </tbody>
          </table>
        )}
      </GlassCard>

      <GlassCard className="p-0 overflow-hidden">
        <div className="px-4 py-2 border-b border-border/40">
          <CardTitle className="text-sm">Recent ingests</CardTitle>
          <CardDescription className="text-xs">
            A line or record that cannot be read is rejected and counted, never failing the batch. An alert already received is not counted twice; one
            received again with a higher severity is escalated and raises a new finding.
          </CardDescription>
        </div>
        {data && data.recentBatches.length === 0 ? (
          <CardDescription className="p-6 text-center text-sm" data-testid="external-alerts-empty">
            Nothing received yet. Alerts appear once a batch is sent to POST /v1/detection/external-alerts.
          </CardDescription>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
                <th className="text-left px-4 py-2 font-medium">Received</th>
                <th className="text-left px-2 py-2 font-medium">Format</th>
                <th className="text-right px-2 py-2 font-medium">Lines or records</th>
                <th className="text-right px-2 py-2 font-medium">New alerts</th>
                <th className="text-right px-2 py-2 font-medium">Already known</th>
                <th className="text-right px-2 py-2 font-medium">Escalated</th>
                <th className="text-right px-2 py-2 font-medium">Rejected</th>
                <th className="text-right px-2 py-2 font-medium">Out of scope</th>
                <th className="text-right px-4 py-2 font-medium">Findings</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recentBatches ?? []).map((b, i) => (
                <tr key={`${b.receivedAt}-${i}`} className="border-b border-border/30" data-testid="external-alert-batch-row">
                  <td className="px-4 py-2 whitespace-nowrap">{when(b.receivedAt)}</td>
                  <td className="px-2 py-2 font-mono text-[11px]">{b.format}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{b.received}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{b.accepted}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{b.alreadyKnown}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{b.escalated ?? 0}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{b.rejected}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{b.outOfScope}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{b.findingsRaised}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </GlassCard>
    </div>
  );
}
